
import { useEffect, useMemo, useState } from "react";

export function Slider({ siteConfig }) {
    const heroSlider = useMemo(() => siteConfig?.hero_slider ?? {}, [siteConfig?.hero_slider]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const DEFAULT_FOREGROUND_WIDTH = 420;
    const DEFAULT_FOREGROUND_HEIGHT = 180;

    const slides = useMemo(() => {
        if (Array.isArray(heroSlider?.slides) && heroSlider.slides.length > 0) {
            return heroSlider.slides;
        }

        return [
            {
                id: 1,
                background_image: heroSlider?.background_image ?? "/images/slider/slider-1.png",
                foreground_image: heroSlider?.foreground_image ?? "/images/slider/slider-logo.svg",
                description: heroSlider?.description ?? "",
            },
        ];
    }, [heroSlider]);

    const autoplay = heroSlider?.settings?.autoplay ?? true;
    const loop = heroSlider?.settings?.loop ?? true;
    const speed = Number(heroSlider?.settings?.speed) || 5000;
    const transition = heroSlider?.settings?.transition ?? "slide";

    useEffect(() => {
        if (currentIndex > slides.length - 1) {
            setCurrentIndex(0);
        }
    }, [currentIndex, slides.length]);

    useEffect(() => {
        if (!autoplay || slides.length < 2) return;
        if (!loop && currentIndex >= slides.length - 1) return;

        const intervalId = setInterval(() => {
            setCurrentIndex((prev) => {
                const nextIndex = prev + 1;
                if (nextIndex >= slides.length) {
                    return loop ? 0 : prev;
                }
                return nextIndex;
            });
        }, speed);

        return () => clearInterval(intervalId);
    }, [autoplay, currentIndex, loop, slides.length, speed]);

    const activeSlide = slides[currentIndex] ?? slides[0];
    const toPositiveNumber = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    };
    const foregroundWidth =
        toPositiveNumber(activeSlide?.foreground_width) ??
        toPositiveNumber(heroSlider?.foreground_width) ??
        DEFAULT_FOREGROUND_WIDTH;
    const foregroundHeight =
        toPositiveNumber(activeSlide?.foreground_height) ??
        toPositiveNumber(heroSlider?.foreground_height) ??
        DEFAULT_FOREGROUND_HEIGHT;
    const description =
        activeSlide?.description ||
        heroSlider?.description ||
        "";

    return (
        <div className="relative w-full h-screen  -mt-[72px] ">
            <div className="absolute inset-0 overflow-hidden">
                {slides.map((slide, index) => (
                    <img
                        key={slide.id ?? index}
                        src={slide?.background_image ?? "/images/slider/slider-1.png"}
                        alt={`Londa Banner ${index + 1}`}
                        className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 ${
                            transition === "fade"
                                ? index === currentIndex
                                    ? "opacity-100"
                                    : "opacity-0"
                                : index === currentIndex
                                ? "translate-x-0 opacity-100"
                                : "translate-x-full opacity-0"
                        }`}
                    />
                ))}
            </div>

            {/* Overlay */}
            <div className="absolute inset-0  bg-opacity-30"></div>

            {/* Text Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center overflow-hidden">
                <div
                    className="foreground-image-wrapper"
                    style={{
                        "--fg-width": `${foregroundWidth}px`,
                        "--fg-ratio": `${foregroundWidth} / ${foregroundHeight}`,
                    }}
                >
                    <img
                        src={activeSlide?.foreground_image ?? "/images/slider/slider-logo.svg"}
                        alt="Londa Banner"
                        className="h-full w-full object-contain"
                    />
                </div>
                {description && (
                    <p className="hidden sm:block text-base sm:text-xl mt-4 sm:mt-6 text-slider max-w-[90vw] px-4">
                        {description}
                    </p>
                )}
            </div>

            {slides.length > 1 && (
                <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 gap-2">
                    {slides.map((slide, index) => (
                        <button
                            key={`dot-${slide.id ?? index}`}
                            type="button"
                            onClick={() => setCurrentIndex(index)}
                            className={`h-2.5 w-2.5 rounded-full transition ${
                                index === currentIndex ? "bg-white" : "bg-white/50"
                            }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
